import { useEffect, useMemo, useState } from "react";
import logo from "./logo.svg";
import "./styles.css";

const passwordRules = "Use at least 8 characters. Avoid obvious or recycled passwords.";
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

const buildApiUrl = (path) => {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path}`;
};

const readTokenFromLocation = () => {
  const params = new URLSearchParams(window.location.search);
  const direct = (params.get("token") || "").trim();
  if (direct) return direct;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return "";
  const hashQuery = hash.includes("?") ? hash.split("?")[1] : hash;
  if (!hashQuery) return "";
  const hashParams = new URLSearchParams(hashQuery);
  return (hashParams.get("token") || "").trim();
};

async function postJson(path, payload) {
  const response = await fetch(buildApiUrl(path), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  let data = {};
  try {
    data = await response.json();
  } catch (err) {
    // Ignore JSON parse errors
  }
  if (!response.ok) {
    const detail = data?.detail || data?.message || "Request failed.";
    throw new Error(detail);
  }
  return data;
}

function App() {
  const initialToken = useMemo(() => readTokenFromLocation(), []);
  const [token] = useState(initialToken);
  const [status, setStatus] = useState("verifying");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setError("Reset link missing or invalid. Please request a new link from the app.");
      return;
    }
    let cancelled = false;
    const validate = async () => {
      try {
        await postJson("/api/clients/auth/reset-password/validate", { token });
        if (!cancelled) {
          setStatus("ready");
          setError("");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("invalid");
          setError(err.message || "Reset link is invalid or expired.");
        }
      }
    };
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const validatePasswords = () => {
    if (password.length < 8) {
      return "Password must be at least 8 characters.";
    }
    if (password !== confirmPassword) {
      return "Passwords do not match.";
    }
    return null;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const validationError = validatePasswords();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      await postJson("/api/clients/auth/reset-password/confirm", {
        token,
        password,
      });
      setSuccessMessage(
        "Your password has been updated. You can return to the JustFacture app and sign in with your new password."
      );
      setPassword("");
      setConfirmPassword("");
      setStatus("success");
    } catch (err) {
      setError(err.message || "Unable to update password. Please request a new link.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderContent = () => {
    if (status === "verifying") {
      return <p>Verifying reset link…</p>;
    }
    if (status === "invalid") {
      return (
        <div className="status-box error">
          <strong>Something went wrong.</strong>
          <p>{error || "Reset link is invalid or expired."}</p>
        </div>
      );
    }
    return (
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="password">New password</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting || status === "success"}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirm">Confirm password</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting || status === "success"}
            required
          />
        </div>
        <small className="helper">{passwordRules}</small>
        <button type="submit" disabled={submitting || status === "success"}>
          {submitting ? "Updating…" : "Update password"}
        </button>
        {error && (
          <div className="status-box error">
            <strong>Heads up:</strong>
            <p>{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="status-box success">
            <strong>Success!</strong>
            <p>{successMessage}</p>
            {/iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
              <a className="open-app-link" href="justfacture://sign-in">
                Open the JustFacture app
              </a>
            )}
          </div>
        )}
      </form>
    );
  };

  return (
    <div className="main-container">
      <div className="card">
        <img src={logo} alt="JustFacture" className="logo" />
        <h1>Reset your password</h1>
        <p>Enter a new password to regain access to your JustFacture account.</p>
        {renderContent()}
        <footer>© {new Date().getFullYear()} JustFacture Inc.</footer>
      </div>
    </div>
  );
}

export default App;
